import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import Skeleton from 'react-loading-skeleton';
import useToken from 'hooks/useToken';
import { useParams } from 'react-router-dom';
import { useToast } from 'context/providers/ToastProvider';

/**
 * Componente para la sección de Preferencias de Facturación
 * Soporta caracteres Unicode (acentos, ñ, símbolos especiales)
 */
const PreferenciasFacturacion = ({
    // Props del formulario
    register,
    setValue,
    selectedValues,
    setSelectedValues,
    clearError,
    errors,
    cargandoForm,

    // Props de datos
    minDate,
    now,

    // Componente DatePicker
    GreetingsDateInput,

    // Nueva prop para vRFC
    vRFC
}) => {
    const { token, setToken } = useToken();
    const { id } = useParams();
    const idOrden = parseInt(id) || 0;
    const [pago, setPago] = useState([]);
        const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const { showErrorToast } = useToast();
    const [formasdePago, setFormasdePago] = useState([]);
    const [tipoMoneda, setTipoMoneda] = useState([]);

    const selectSerieBasedOnFormaPago = async (tipoDocumento, formaPago) => {
        let serie = "";
        if (vRFC === "CACF9111245G4") {
            serie = "A";
        } else if (tipoDocumento === "Ingreso" && formaPago && vRFC) {
            if (formaPago === "01") { // Efectivo
                serie = "EFE";
            } else if (formaPago === "03") { // Transferencia
                serie = "BB";
            } else if (["04", "28"].includes(formaPago)) { // Tarjeta de crédito o débito
                serie = "STR"; // Asumiendo STR para tarjetas
            } else if (formaPago === "99") { // Por definir
                serie = "CRE";
            } else {
                // Para otros intermediarios, usar PPL
                serie = "PPL";
            }
        }
        if (serie) {
            setValue("vSerie", serie);
            setSelectedValues(prev => ({ ...prev, vSerie: serie }));
            // Consultar el siguiente folio
            const siguienteFolio = await fetchSiguienteFolio(serie, vRFC);
            if (siguienteFolio) {
                setValue("vFolio", siguienteFolio.toString(), { shouldValidate: true });
            }
        }
    };

    //sOLO ENTRA UNA VEZ
    const selectSerieBasedOnMedioPago = async (medioPago) => {
        let serie = "";
        if (vRFC === "CACF9111245G4") {
            serie = "A";
        } else if (medioPago) {
            if (medioPago === 1) { // Banco
                serie = "BB";
            } else if (medioPago === 2) { // STR
                serie = "STR";
            } else if (medioPago === 3) { // PAYPAL
                serie = "PPL";
            } else if (medioPago === 4) { // Western Union
                serie = "WU";
            } else if (medioPago === 5) { // Efectivo
                serie = "EFE";
            }
        }
        if (serie) {
            setValue("vSerie", serie);
            setSelectedValues(prev => ({ ...prev, vSerie: serie }));
            // Consultar el siguiente folio
            const siguienteFolio = await fetchSiguienteFolio(serie, vRFC);
            if (siguienteFolio) {
                setValue("vFolio", siguienteFolio.toString(), { shouldValidate: true });
            }
        }
    };

    /**
     * Llama al endpoint para obtener el siguiente folio de una serie
     * @param {string} serie
     * @param {string} vRFC
     * @returns {Promise<number|null>}
     */
    async function fetchSiguienteFolio(serie, vRFC) {
        // Validar que ambos parámetros sean válidos
        if (!serie || !vRFC || vRFC.trim() === '') {
            console.warn('fetchSiguienteFolio: serie o vRFC no válidos', { serie, vRFC });
            return null;
        }

        try {
            const response = await fetch(`/api/contabilidad/facturas/FormularioFactura/ObtenerSiguienteFolio?serie=${encodeURIComponent(serie)}&vRFC=${encodeURIComponent(vRFC)}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                if (data && data.siguienteFolio) {
                    return data.siguienteFolio;
                }
            } else if (response.status === 401) {
                console.error('Unauthorized: Token inválido o expirado');
            }
        } catch (e) {
            console.log(e);
        }
        return null;
    }

    /**
     * Llama al endpoint para obtener la información de pago de una orden
     * @param {number} idOrden
     */
    async function fetchInformacionPago(idOrden) {
        try {
            const response = await fetch(`/api/contabilidad/facturas/FacturaOrden/ObtenerInformacionPago?idOrden=${encodeURIComponent(idOrden)}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.ok) {
                const data = await response.json();
                const dataParsed = JSON.parse(data.data);
                if (data.iResponseType === 1) {
                    setPago(dataParsed.table1);
                    if (dataParsed.table1.length > 0) {
                        const pagoData = dataParsed.table1[0];
                        setSelectedValues(prev => ({ ...prev, vMoneda: pagoData.vMoneda }));
                        setValue("dTipoCambio", pagoData.dTipoCambio);
                     
                        const medioPago = pagoData.idCatMedioPago;
                        selectSerieBasedOnMedioPago(medioPago);
                        setValue("vCondiciones", (medioPago == 2 || medioPago == 3) ? pagoData.vReferencia : "");
                    }
                }
            } else if (response.status === 401) {
                console.error('Unauthorized: Token inválido o expulsado');
            }
        } catch (e) {
            console.log(e);
        }
    }


    /**
     * Obtiene los Formas Pago
     */
    const obtenerFormasdePago = async () => {

        const response = await fetch("/api/contabilidad/CatalogoSAT/ObtenerFormasDePago",
            {
                method: 'POST',
                headers:
                {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            })
            .catch((error) => {
                
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                //console.log('Hubo un problema con la peticion Fetch:' + error);

            });

        if (response.ok) {
            //console.log("==== OBTENER FORMAS DE PAGO ====");

            // console.log("response: ", response);
            const data = await response.json();
            //console.log("dataRF: ", data);

            if (data.iResponseType == 1) {

                var ObjDetalles = JSON.parse(data.detalles);

                // console.log("ObjDetallesFP:", ObjDetalles);

                setFormasdePago(ObjDetalles.table2);


            } else {
                
                showErrorToast(data.vMessage);
            }

        }
        else {
            
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    }

    /**
    * Obtiene las monedas del Cat Sat
    */
    const ObtenerCatMonedas = async () => {
        if (tipoMoneda.length > 0) return; // Ya están cargadas

        const response = await fetch("/api/contabilidad/CatalogoSAT/ObtenerCatMonedas",
            {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            })
            .catch((error) => {
                
                showErrorToast('Hubo un problema con la peticion Fetch:' + error);
                //console.log('Hubo un problema con la peticion Fetch:' + error);

            });

        if (response.ok) {
            //console.log("==== OBTENER MONEDA ====");

            // console.log("response: ", response);
            const data = await response.json();
            // console.log("dataRF: ", data);

            if (data.iResponseType == 1) {

                var ObjDetalles = JSON.parse(data.detalles);

                //console.log("ObjDetallesTM:", ObjDetalles);

                setTipoMoneda(ObjDetalles.table2);


            } else {
                
                showErrorToast(data.vMessage);
            }

        }
        else {
            
            showErrorToast(response.status + ' ' + response.statusText);
            if (response.status == 401) {
                setToken(null);
                location.href = "";
            }
        }
    }

    /**
    * Establece el valor a la fecha
    */
    const handleDateChange = (selectedDate) => {
        if (!selectedDate) {
            setValue("dFechaEmision", null);
            setSelectedValues(prev => ({ ...prev, dFechaEmision: null }));
            return;
        }

        clearError("dFechaEmision"); // Limpiar error si se selecciona una fecha

        // Convierte el valor a un objeto Date si no lo es
        const dateObj = typeof selectedDate === "string"
            ? new Date(selectedDate)
            : selectedDate;

        // Formatea la fecha en el formato correcto: yyyy-MM-dd HH:mm:ss (sin la T)
        // Usar los métodos locales para evitar conversiones de zona horaria
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        const hours = String(dateObj.getHours()).padStart(2, "0");
        const minutes = String(dateObj.getMinutes()).padStart(2, "0");
        const seconds = String(dateObj.getSeconds()).padStart(2, "0");

        const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

        setSelectedValues(prev => ({ ...prev, dFechaEmision: formattedDate }));

        // Actualiza en react-hook-form con el formato correcto
        setValue("dFechaEmision", formattedDate);
    }


    useEffect(() => {
        // Al cargar el componente, si hay una serie seleccionada, obtener el siguiente folio automáticamente
        (async () => {
            const serieInicial = selectedValues.vSerie;
            if (serieInicial && vRFC) {
                const siguienteFolio = await fetchSiguienteFolio(serieInicial, vRFC);
                if (siguienteFolio) {
                    setValue("vFolio", siguienteFolio.toString(), { shouldValidate: true });
                }
            }
        })();
    }, [selectedValues.vSerie, vRFC]); // Executed when serie or vRFC changes

    useEffect(() => {
        const tipoDocumento = selectedValues.vTipoDocumento;
        if (vRFC) {
            selectSerieBasedOnFormaPago(tipoDocumento, selectedValues.vClavePago);
        }
    }, [selectedValues.vTipoDocumento, selectedValues.vClavePago]);

    useEffect(() => {
        if (idOrden) {
            fetchInformacionPago(idOrden);
        }
    }, [selectedValues.vClavePago, vRFC]); // Ejecutar solo una vez al montar el componente

    useEffect(() => {
        //fecha de emision
        const dateA = new Date(Date.now() - 5 * 60 * 1000);
        handleDateChange(dateA);
        obtenerFormasdePago();
        ObtenerCatMonedas();
    },[]);
    return (
        <>
            <Col className="mt-3 mt-md-0" lg={6} style={{ paddingLeft: "15px" }}>
                <h5 className="seccion-titulo mt-3 mb-3 bg-light p-2 rounded text-dark">Preferencias de Facturaci&oacute;n</h5>
                                <Row className="mb-1">
                    <Col xs={12} md={4} className="mb-2">
                        <Form.Group>
                            <Form.Label className="form-label-profesional">Tipo de Documento</Form.Label>
                            {
                                cargandoForm ?
                                    <Skeleton />
                                    :
                                    <Form.Select className="form-control-profesional" size="sm"
                                        value={selectedValues.vTipoDocumento || ""}
                                        isInvalid={!!errors.vTipoDocumento}
                                        {...register("vTipoDocumento", { required: "Seleccione un tipo de documento" })}
                                        onChange={(e) => {
                                            setValue("vTipoDocumento", e.target.value); // Actualiza el tipo de documento
                                            setSelectedValues((prev) => ({ ...prev, vTipoDocumento: e.target.value }));
                                            setValue("vSerie", ""); // Reinicia la selección de "Serie"
                                            setSelectedValues((prev) => ({ ...prev, vSerie: "" }));
                                            setValue("vFolio", ""); // Reinicia el folio
                                            if (e.target.value && vRFC) {
                                                clearError("vTipoDocumento");
                                                if (e.target.value === "Pago") {
                                                    const defaultSerie = "P";
                                                    setValue("vSerie", defaultSerie);
                                                    setSelectedValues((prev) => ({ ...prev, vSerie: defaultSerie }));
                                                    // Consultar el siguiente folio
                                                    fetchSiguienteFolio(defaultSerie, vRFC).then(siguienteFolio => {
                                                        if (siguienteFolio) {
                                                            setValue("vFolio", siguienteFolio.toString(), { shouldValidate: true });
                                                        }
                                                    });
                                                } else if (e.target.value === "Egreso") {
                                                    const defaultSerie = "E";
                                                    setValue("vSerie", defaultSerie);
                                                    setSelectedValues((prev) => ({ ...prev, vSerie: defaultSerie }));
                                                    // Consultar el siguiente folio
                                                    fetchSiguienteFolio(defaultSerie, vRFC).then(siguienteFolio => {
                                                        if (siguienteFolio) {
                                                            setValue("vFolio", siguienteFolio.toString(), { shouldValidate: true });
                                                        }
                                                    });
                                                } else if (e.target.value === "Ingreso") {
                                                    if (pago.length > 0) {
                                                        selectSerieBasedOnMedioPago(pago[0].idCatMedioPago);
                                                    } else {
                                                        selectSerieBasedOnFormaPago(e.target.value, selectedValues.vClavePago);
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <option value="">Seleccionar</option>
                                        <option value="Ingreso">I &rarr; Ingreso</option>
                                        <option value="Egreso">E &rarr; Egreso (Nota de cr&eacute;dito)</option>
                                    </Form.Select>
                            }
                            {errors.vTipoDocumento && (
                                <Form.Text className="form-error-text">{errors.vTipoDocumento}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>

                    <Col xs={12} md={4} className="mb-2">
                        <Form.Group>
                            <Form.Label className="form-label-profesional">Serie</Form.Label>
                            {
                                cargandoForm ?
                                    <Skeleton />
                                    :
                                    <Form.Select className="form-control-profesional" size="sm"
                                        value={selectedValues.vSerie || ""}
                                        isInvalid={!!errors.vSerie}
                                        {...register("vSerie", { required: "Seleccione una serie" })}
                                        onChange={async (e) => {
                                            const serie = e.target.value;
                                            setValue("vSerie", serie); // Actualiza el estado
                                            setSelectedValues((prev) => ({ ...prev, vSerie: serie }));
                                            if (serie && vRFC) {
                                                clearError("vSerie");
                                                // Consultar el siguiente folio al cambiar la serie
                                                const siguienteFolio = await fetchSiguienteFolio(serie, vRFC);
                                                if (siguienteFolio) {
                                                    setValue("vFolio", siguienteFolio.toString(), { shouldValidate: true });
                                                } else {
                                                    setValue("vFolio", "", { shouldValidate: true });
                                                }
                                            } else {
                                                setValue("vFolio", "", { shouldValidate: true });
                                            }
                                        }}
                                    >
                                        <option value="">Seleccionar</option>
                                        {selectedValues.vTipoDocumento === "Ingreso" && (
                                            <>
                                                <option value="BB">BB &rarr; Banco</option>
                                                <option value="STR">STR &rarr; Stripe</option>
                                                <option value="PPL">PPL &rarr; PayPal</option>
                                                <option value="WU">WU &rarr; Western Union</option>
                                                <option value="EFE">EFE &rarr; Efectivo</option>
                                                <option value="CRE">CRE &rarr; Por definir</option>
                                                <option value="A">A &rarr; General</option>
                                            </>
                                        )}
                                        {selectedValues.vTipoDocumento === "Pago" && (
                                            <option value="P">P &rarr; Complemento de Pago</option>
                                        )}
                                        {selectedValues.vTipoDocumento === "Egreso" && (
                                            <option value="E">E &rarr; Nota de Cr&eacute;dito (Egreso)</option>
                                        )}
                                    </Form.Select>
                            }
                            {errors.vSerie && (
                                <Form.Text className="form-error-text">{errors.vSerie}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>

                    <Col xs={12} md={4} className="mb-2">
                        {/* Folio */}
                        <Form.Group className="">
                            <Form.Label className="form-label-profesional">Folio</Form.Label>
                            {
                                cargandoForm ?
                                    <Skeleton />
                                    :
                                    <Form.Control className="form-control-profesional" size="sm"
                                        type="text"
                                        {...register("vFolio", {
                                            required: "El folio es obligatorio",
                                            pattern: {
                                                value: /^\d+$/,
                                                message: "El folio solo puede contener números"
                                            }
                                        })}
                                        isInvalid={!!errors.vFolio}
                                    />
                            }
                            {errors.vFolio && (
                                <Form.Text className="form-error-text">{errors.vFolio.message}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                {/* Método de Pago y Forma de Pago en la misma fila */}
                <Row className="mb-1">
                    <Col xs={12} md={6} className="mb-2">
                        <Form.Group>
                            <Form.Label className="form-label-profesional">M&eacute;todo de Pago</Form.Label>
                            {
                                cargandoForm ?
                                    <Skeleton />
                                    :
                                    <Form.Select className="form-control-profesional" size="sm"
                                        isInvalid={!!errors.idMetodoPago}
                                        {...register("idMetodoPago", {
                                            onChange: (e) => {
                                                const value = e.target.value;
                                                if (value) {
                                                    clearError("idMetodoPago");
                                                }
                                                if (value === "PPD") {
                                                    setSelectedValues((prev) => ({ ...prev, vClavePago: "99" }));
                                                    setValue("vClavePago", "99", { shouldValidate: true });
                                                    clearError("vClavePago");
                                                } else if (value === "PUE") {
                                                    setSelectedValues((prev) => ({ ...prev, vClavePago: "" }));
                                                    setValue("vClavePago", "", { shouldValidate: true });
                                                }
                                            }
                                        })}
                                    >
                                        <option value="">Seleccione un M&eacute;todo de Pago</option>
                                        <option value="PUE">PUE </option>
                                        <option value="PPD">PPD  </option>
                                    </Form.Select>
                            }
                            {errors.idMetodoPago && (
                                <Form.Text className="form-error-text">{errors.idMetodoPago}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>

                    <Col xs={12} md={6} className="mb-2">
                        <Form.Group>
                            <Form.Label className="form-label-profesional">Forma de Pago</Form.Label>
                            {
                                cargandoForm ?
                                    <Skeleton />
                                    :
                                    <Form.Select className="form-control-profesional" size="sm"
                                        value={selectedValues.vClavePago || ""}
                                        isInvalid={!!errors.vClavePago}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSelectedValues((prev) => ({ ...prev, vClavePago: value }));

                                            // Si vClavePago es 99 (Por definir), automáticamente selecciona PPD en Método de Pago
                                            if (value === "99") {
                                                setValue("idMetodoPago", "PPD");
                                            } else {
                                                setValue("idMetodoPago", "PUE");
                                            }

                                            if (e.target.value) {
                                                clearError("vClavePago");
                                                clearError("idMetodoPago");
                                                if (vRFC) {
                                                    selectSerieBasedOnFormaPago(selectedValues.vTipoDocumento, e.target.value);
                                                } else {
                                                    showErrorToast('Debe seleccionar un RFC para que se asigne la serie automáticamente.');

                                                }
                                            }
                                        }}
                                    >
                                        <option value="">Elija una forma de pago</option>
                                        {formasdePago.map((pago, index) => (
                                            <option key={index} value={pago.vClave}>
                                                {pago.vClave} - {pago.vDescripcionFormaPago}
                                            </option>
                                        ))}
                                    </Form.Select>
                            }
                            {errors.vClavePago && (
                                <Form.Text className="form-error-text">{errors.vClavePago}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-1">
                    <Col xs={12} md={6} className="mb-2">
                        <Form.Group>
                            <Form.Label className="form-label-profesional">Moneda</Form.Label>
                            {
                                cargandoForm ?
                                    <Skeleton />
                                    :
                                    <Form.Select className="form-control-profesional" size="sm"
                                        value={selectedValues.vMoneda || ""}
                                        isInvalid={!!errors.vMoneda}
                                        onChange={(e) => {
                                            const monedaSeleccionada = e.target.value;
                                            setSelectedValues((prev) => ({
                                                ...prev,
                                                vMoneda: monedaSeleccionada,
                                            }));

                                            if (monedaSeleccionada === "MXN") {
                                                setValue("dTipoCambio", 1, { shouldValidate: true });
                                                clearError("dTipoCambio");
                                            } else {
                                                setValue("dTipoCambio", "", { shouldValidate: true });
                                            }

                                            // trigger("dTipoCambio"); // Dispara la validación manualmente

                                            if (e.target.value) {
                                                // setErrors((prevErrors) => ({ ...prevErrors, vMoneda: undefined }));
                                            }
                                        }}
                                    >
                                        <option value="">Seleccione una moneda</option>
                                        {Array.isArray(tipoMoneda) && tipoMoneda.map((moneda, index) => (
                                            <option key={index} value={moneda.vClaveMoneda}>
                                                {moneda.vClaveMoneda}
                                            </option>
                                        ))}
                                    </Form.Select>
                            }
                            {errors.vMoneda && (
                                <Form.Text className="form-error-text">{errors.vMoneda}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>

                    {/* Solo mostrar el campo Tipo de Cambio si la moneda NO es MXN */}
                    {selectedValues.vMoneda && selectedValues.vMoneda !== "MXN" && (
                        <Col xs={12} md={6} className="mb-2">
                            <Form.Group>
                                <Form.Label className="form-label-profesional">Tipo de Cambio <span className="form-error-text">*</span></Form.Label>
                                {
                                    cargandoForm ?
                                        <Skeleton />
                                        :
                                        <Form.Control className="form-control-profesional" size="sm"
                                            type="number"
                                            step="any"
                                            {...register("dTipoCambio", {
                                                min: { value: 0.01, message: "El tipo de cambio debe ser mayor a 0" },
                                                required: selectedValues.vMoneda !== "MXN" ? "El tipo de cambio es obligatorio para monedas distintas a MXN" : false,
                                                valueAsNumber: true,
                                            })}
                                            onKeyDown={(e) => {
                                                if (e.key === "-" || e.key === "e" || e.key === "E") {
                                                    e.preventDefault();
                                                }
                                            }}
                                            onChange={(e) => {
                                                const nuevoValor = parseFloat(e.target.value) || "";
                                                setValue("dTipoCambio", nuevoValor, { shouldValidate: true });
                                                // trigger("dTipoCambio"); // Actualiza el estado de error
                                                // Limpiar error al seleccionar un valor
                                                if (e.target.value) {
                                                    // setErrors((prevErrors) => ({ ...prevErrors, dTipoCambio: undefined }));
                                                }

                                                if (e.target.value) {
                                                    clearError("dTipoCambio");
                                                }
                                            }}
                                            isInvalid={!!errors.dTipoCambio}
                                            placeholder="Ingrese el tipo de cambio"
                                        />
                                }
                                {errors.dTipoCambio && (
                                    <Form.Text className="form-error-text">{errors.dTipoCambio}</Form.Text>
                                )}
                            </Form.Group>
                        </Col>
                    )}
                </Row>

                <Row className="mb-1">
                        <Col xs={12} md={6} className="mb-2">
                            <Form.Group className="mb-3">
                                <Form.Label className="form-label-profesional">Condiciones</Form.Label>
                                {
                                    cargandoForm ?
                                        <Skeleton />
                                        :
                                        <Form.Control className="form-control-profesional" size="sm"
                                            defaultValue=""
                                            type="text"
                                            {...register("vCondiciones",
                                                {
                                                    maxLength: { value: 500, message: "El texto no puede superar los 500 caracteres" },
                                                })
                                            }
                                        />
                                }
                            </Form.Group>
                        </Col>

                    <Col xs={12} md={6} className="mb-2">
                        <Form.Group>
                            <Form.Label className="form-label-profesional">Fecha de Emisi&oacute;n</Form.Label>
                            {
                                cargandoForm ?
                                    <Skeleton />
                                    :
                                    <DatePicker
                                        selected={selectedValues.dFechaEmision ? new Date(selectedValues.dFechaEmision) : fiveMinutesAgo}
                                        onChange={handleDateChange}
                                        dateFormat="yyyy-MM-dd HH:mm:ss"
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={1}
                                        minDate={minDate}
                                        maxDate={now}
                                        className={`form-control ${errors.fechaEmision ? "is-invalid" : ""}`}
                                        customInput={<GreetingsDateInput />}
                                        popperModifiers={[
                                            {
                                                name: "preventOverflow",
                                                options: { boundary: "viewport" },
                                            },
                                            {
                                                name: "offset",
                                                options: { offset: [0, -290] },
                                            },
                                        ]}
                                    />
                            }
                            {errors.dFechaEmision && (
                                <Form.Text className="form-error-text">{errors.dFechaEmision}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
            </Col>
        </>
    );
};

PreferenciasFacturacion.propTypes = {
    // Props del formulario
    register: PropTypes.func.isRequired,
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    selectedValues: PropTypes.object.isRequired,
    setSelectedValues: PropTypes.func.isRequired,
    clearError: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    cargandoForm: PropTypes.bool.isRequired,

    // Props de datos
    minDate: PropTypes.instanceOf(Date),
    now: PropTypes.instanceOf(Date),

    // Componente DatePicker
    GreetingsDateInput: PropTypes.elementType.isRequired,

    // Nueva prop para vRFC
    vRFC: PropTypes.string.isRequired
};

export default PreferenciasFacturacion;



